interface FormStructureSummaryProps {
  title?: string;
  isLoading: boolean;
  errorMessage?: string;
  sectionCount?: number;
  subsectionCount?: number;
  fieldCount?: number;
}

export function FormStructureSummary({
  title = "Form Structure",
  isLoading,
  errorMessage,
  sectionCount = 0,
  subsectionCount = 0,
  fieldCount = 0,
}: FormStructureSummaryProps) {
  const Heading = (
    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
      <div className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></div>
      {title}
    </h4>
  );

  if (isLoading) {
    return (
      <div>
        {Heading}
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 border border-purple-200 rounded-lg bg-white">
            <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 w-6 rounded-full bg-gray-200 animate-pulse" />
          </div>
          <div className="flex items-center justify-between p-4 border border-blue-200 rounded-lg bg-white">
            <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 w-6 rounded-full bg-gray-200 animate-pulse" />
          </div>
          <div className="flex items-center justify-between p-4 border border-green-200 rounded-lg bg-white">
            <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 w-6 rounded-full bg-gray-200 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div>
        {Heading}
        <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg">
          Unable to load the form structure.
          <span className="block text-[11px] text-red-500 mt-1">{errorMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {Heading}
      <div className="space-y-3">
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
          <span className="text-sm text-gray-700 font-medium">Sections</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <span className="text-xs font-bold text-white">{sectionCount}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
          <span className="text-sm text-gray-700 font-medium">Subsections</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <span className="text-xs font-bold text-white">{subsectionCount}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
          <span className="text-sm text-gray-700 font-medium">Total Fields</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <span className="text-xs font-bold text-white">{fieldCount}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormStructureSummary;
